import { Button, Card, Input, Select } from "components"
import type { Dispatch, SetStateAction } from "react"
import { useNavigate } from "react-router-dom"
import type { MovementFilter } from "../types/inventoryMovement"
import {
  CONTROL_STATUS_OPTIONS,
  LOCATION_OPTIONS,
  TRANSACTION_TYPE_OPTIONS
} from "../types/inventoryMovement"

type FilterSectionProps = {
  filter: MovementFilter
  setFilter: Dispatch<SetStateAction<MovementFilter>>
  onSearchReport: () => void
  onResetFilter: () => void
}

type Opt = { value: string; label: string }

function optionByValue(options: readonly Opt[], value: string): Opt | null {
  return options.find((o) => o.value === value) ?? null
}

export function FilterSection({
  filter,
  setFilter,
  onSearchReport,
  onResetFilter
}: FilterSectionProps) {
  const navigate = useNavigate()

  return (
    <Card className="shadow-sm">
      <div className="flex flex-col gap-1 border-b border-[#dadada] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-sm font-bold uppercase tracking-wide text-base-content">
          Report Filter
        </h3>
        <p className="text-xs text-base-content/60">
          Inventory / Report / Movement Control
        </p>
      </div>

      <Card.Body className="gap-0 px-4 py-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <Input
            label="Date From"
            type="date"
            className="w-full"
            value={filter.dateFrom}
            onChange={(e) =>
              setFilter((p) => ({ ...p, dateFrom: e.target.value }))
            }
          />
          <Input
            label="Date To"
            type="date"
            className="w-full"
            value={filter.dateTo}
            onChange={(e) =>
              setFilter((p) => ({ ...p, dateTo: e.target.value }))
            }
          />
          <Select
            label="Location"
            placeholder="Select location"
            className="w-full"
            options={[...LOCATION_OPTIONS]}
            value={optionByValue(LOCATION_OPTIONS, filter.location)}
            onChange={(v) => {
              if (!v) return
              setFilter((p) => ({ ...p, location: v.value }))
            }}
          />
          <Select
            label="Control Status"
            placeholder="Select status"
            className="w-full"
            options={[...CONTROL_STATUS_OPTIONS]}
            value={optionByValue(CONTROL_STATUS_OPTIONS, filter.controlStatus)}
            onChange={(v) => {
              if (!v) return
              setFilter((p) => ({
                ...p,
                controlStatus: v.value as MovementFilter["controlStatus"]
              }))
            }}
          />
          <Select
            label="Transaction Type"
            placeholder="Select type"
            className="w-full md:col-span-2 xl:col-span-2"
            options={[...TRANSACTION_TYPE_OPTIONS]}
            value={optionByValue(
              TRANSACTION_TYPE_OPTIONS,
              filter.transactionType
            )}
            onChange={(v) => {
              if (!v) return
              setFilter((p) => ({ ...p, transactionType: v.value }))
            }}
          />
          <Input
            label="Search"
            className="w-full md:col-span-2 xl:col-span-2"
            placeholder="Reference, note, created by..."
            value={filter.search}
            onChange={(e) =>
              setFilter((p) => ({ ...p, search: e.target.value }))
            }
          />
        </div>

        <div className="mt-4 flex flex-wrap gap-2 border-t border-[#dadada] pt-4">
          <Button
            type="button"
            color="primary"
            size="sm"
            onClick={onSearchReport}>
            Search Report
          </Button>
          <Button
            type="button"
            color="outline-secondary"
            size="sm"
            onClick={onResetFilter}>
            Reset Filter
          </Button>
          <Button
            type="button"
            color="outline-secondary"
            size="sm"
            onClick={() =>
              navigate("/inventory/report/item-detail-stock-global")
            }>
            Back to Item Detail
          </Button>
        </div>
      </Card.Body>
    </Card>
  )
}
